export const UI_TRANSLATION_WORKSPACE_SCHEMA = 1;
export const UI_TRANSLATION_WORKSPACE_MAX_BYTES = 512 * 1024;
export const UI_TRANSLATION_WORKSPACE_MAX_CHANGES = 1000;
export const UI_TRANSLATION_MAX_VALUE_LENGTH = 10000;
export const UI_TRANSLATION_MAX_VALUE_BYTES = 4096;
export const UI_TRANSLATION_WORKSPACE_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;

const WORKSPACE_PREFIX = 'jdp.uiTranslations.workspace';
const ROLLBACK_ATTEMPT_PREFIX = 'jdp.uiTranslations.rollbackAttempt';
const ALLOWED_ACTIONS = ['SET_VALUE', 'RESET_TO_GENERATED'];
const ALLOWED_OWNERSHIP = ['GENERATED', 'HUMAN'];
const UNSAFE_KEY_SEGMENTS = ['__proto__', 'prototype', 'constructor'];
const STORAGE_WRITE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;
const STORAGE_LOCK_PREFIX = 'jdp.uiTranslations.storage';

const isObject = (value) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const utf8ByteLength = (value) => {
  let length = 0;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < 0x80) {
      length += 1;
    } else if (code < 0x800) {
      length += 2;
    } else if (code >= 0xd800 && code <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (next >= 0xdc00 && next <= 0xdfff) index += 1;
      length += 4;
    } else {
      length += 3;
    }
  }
  return length;
};

const normalizeEnvironment = (environment) => {
  const raw = String(environment || '').trim();
  if (!raw) return '';
  try {
    const baseOrigin =
      typeof window !== 'undefined' && window.location
        ? window.location.origin
        : 'http://localhost';
    const parsed = new URL(raw, baseOrigin);
    return `${parsed.origin}${parsed.pathname}`.replace(/\/+$/, '');
  } catch {
    return raw.replace(/\/+$/, '');
  }
};

const encodeWorkspacePart = (value) => encodeURIComponent(String(value).trim());

const isSafeKey = (key) => {
  if (
    typeof key !== 'string' ||
    key.length < 1 ||
    key.length > 256 ||
    !/^[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*$/.test(key)
  ) {
    return false;
  }
  return !key
    .split('.')
    .some((segment) => UNSAFE_KEY_SEGMENTS.includes(segment.toLowerCase()));
};

const normalizeOwnership = (ownership) =>
  ALLOWED_OWNERSHIP.includes(ownership) ? ownership : null;

const normalizeChange = (change) => {
  if (!isObject(change) || !isSafeKey(change.key)) return null;
  if (!ALLOWED_ACTIONS.includes(change.action)) return null;
  if (typeof change.baseHash !== 'string' || change.baseHash.length > 256) {
    return null;
  }
  if (
    change.sourceHash !== undefined &&
    (typeof change.sourceHash !== 'string' || change.sourceHash.length > 256)
  ) {
    return null;
  }
  if (
    change.baseOwnership !== undefined &&
    change.baseOwnership !== null &&
    !ALLOWED_OWNERSHIP.includes(change.baseOwnership)
  ) {
    return null;
  }
  if (
    change.action === 'SET_VALUE' &&
    (typeof change.value !== 'string' ||
      change.value.length > UI_TRANSLATION_MAX_VALUE_LENGTH)
  ) {
    return null;
  }

  return {
    key: change.key,
    action: change.action,
    baseHash: change.baseHash,
    baseOwnership: normalizeOwnership(change.baseOwnership),
    sourceHash: typeof change.sourceHash === 'string' ? change.sourceHash : '',
    ...(change.action === 'SET_VALUE' ? {value: change.value} : {}),
  };
};

const normalizePendingPublication = (candidate) => {
  if (candidate === undefined || candidate === null) return null;
  if (!isObject(candidate)) return undefined;
  if (
    typeof candidate.fingerprint !== 'string' ||
    !/^[a-f0-9:-]{1,128}$/i.test(candidate.fingerprint) ||
    typeof candidate.idempotencyKey !== 'string' ||
    candidate.idempotencyKey.length < 8 ||
    candidate.idempotencyKey.length > 200
  ) {
    return undefined;
  }
  return {
    fingerprint: candidate.fingerprint,
    idempotencyKey: candidate.idempotencyKey,
  };
};

const normalizeStorageIdentity = (candidate) => {
  const storageRevision =
    candidate.storageRevision === undefined ? 0 : candidate.storageRevision;
  const writeToken =
    candidate.writeToken === undefined ? '' : candidate.writeToken;
  if (
    !Number.isSafeInteger(storageRevision) ||
    storageRevision < 0 ||
    typeof writeToken !== 'string' ||
    (writeToken && !STORAGE_WRITE_TOKEN_PATTERN.test(writeToken)) ||
    (storageRevision === 0 && writeToken) ||
    (storageRevision > 0 && !writeToken)
  ) {
    return null;
  }
  return {storageRevision, writeToken};
};

const createStorageWriteToken = () =>
  `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;

const runWithStorageLock = async (lockManager, key, callback) => {
  if (!lockManager || typeof lockManager.request !== 'function') {
    return {lockUnavailable: true};
  }
  try {
    return await lockManager.request(
      `${STORAGE_LOCK_PREFIX}:${key}`,
      {mode: 'exclusive'},
      callback
    );
  } catch {
    return {lockUnavailable: true};
  }
};

export const createUiTranslationWorkspaceKey = ({
  environment,
  userId,
  locale,
  namespace,
}) => {
  const normalizedEnvironment = normalizeEnvironment(environment);
  if (!normalizedEnvironment || !userId || !locale || !namespace) {
    throw new Error('A complete UI translation workspace identity is required.');
  }
  return [
    WORKSPACE_PREFIX,
    `v${UI_TRANSLATION_WORKSPACE_SCHEMA}`,
    encodeWorkspacePart(normalizedEnvironment),
    encodeWorkspacePart(userId),
    encodeWorkspacePart(locale),
    encodeWorkspacePart(namespace),
  ].join(':');
};

export const createUiTranslationRollbackAttemptKey = ({
  environment,
  userId,
  locale,
}) => {
  const normalizedEnvironment = normalizeEnvironment(environment);
  if (!normalizedEnvironment || !userId || !locale) {
    throw new Error('A complete UI translation rollback identity is required.');
  }
  return [
    ROLLBACK_ATTEMPT_PREFIX,
    'v1',
    encodeWorkspacePart(normalizedEnvironment),
    encodeWorkspacePart(userId),
    encodeWorkspacePart(locale),
  ].join(':');
};

export const normalizeUiTranslationWorkspace = (workspace) => {
  if (!isObject(workspace)) return null;
  if (workspace.schemaVersion !== UI_TRANSLATION_WORKSPACE_SCHEMA) return null;
  if (
    typeof workspace.catalogVersion !== 'string' ||
    !workspace.catalogVersion ||
    workspace.catalogVersion.length > 256
  ) {
    return null;
  }
  if (
    !Number.isSafeInteger(workspace.baseRevision) ||
    workspace.baseRevision < 0
  ) {
    return null;
  }
  if (
    !Array.isArray(workspace.changes) ||
    workspace.changes.length > UI_TRANSLATION_WORKSPACE_MAX_CHANGES
  ) {
    return null;
  }
  const savedAtTime = Date.parse(workspace.savedAt);
  if (
    typeof workspace.savedAt !== 'string' ||
    !Number.isFinite(savedAtTime) ||
    savedAtTime > Date.now() + 24 * 60 * 60 * 1000 ||
    Date.now() - savedAtTime > UI_TRANSLATION_WORKSPACE_MAX_AGE_MS
  ) {
    return null;
  }
  const pendingPublication = normalizePendingPublication(
    workspace.pendingPublication
  );
  if (pendingPublication === undefined) return null;
  const storageIdentity = normalizeStorageIdentity(workspace);
  if (!storageIdentity) return null;

  const changes = workspace.changes.map(normalizeChange);
  if (changes.some((change) => !change)) return null;
  const changeKeys = changes.map((change) => change.key);
  if (new Set(changeKeys).size !== changeKeys.length) return null;
  changes.sort((left, right) => left.key.localeCompare(right.key));

  const normalized = {
    schemaVersion: UI_TRANSLATION_WORKSPACE_SCHEMA,
    catalogVersion: workspace.catalogVersion,
    baseRevision: workspace.baseRevision,
    changes,
    savedAt: workspace.savedAt,
    ...storageIdentity,
    ...(pendingPublication ? {pendingPublication} : {}),
  };
  const serialized = JSON.stringify(normalized);
  return utf8ByteLength(serialized) <= UI_TRANSLATION_WORKSPACE_MAX_BYTES
    ? normalized
    : null;
};

export const createUiTranslationWorkspace = ({
  catalogVersion,
  baseRevision,
  changes = [],
}) =>
  normalizeUiTranslationWorkspace({
    schemaVersion: UI_TRANSLATION_WORKSPACE_SCHEMA,
    catalogVersion,
    baseRevision,
    changes,
    savedAt: new Date().toISOString(),
  });

const safelyRemoveWorkspace = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch {
    // A blocked storage implementation should not prevent the editor loading.
  }
};

const readStoredWorkspaceForWrite = (storage, key) => {
  const serialized = storage.getItem(key);
  if (!serialized) return {workspace: null, serialized: null};
  if (utf8ByteLength(serialized) > UI_TRANSLATION_WORKSPACE_MAX_BYTES) {
    return {workspace: null, serialized, invalid: true};
  }
  try {
    const workspace = normalizeUiTranslationWorkspace(JSON.parse(serialized));
    return workspace
      ? {workspace, serialized}
      : {workspace: null, serialized, invalid: true};
  } catch {
    return {workspace: null, serialized, invalid: true};
  }
};

export const loadUiTranslationWorkspace = async (
  storage,
  key,
  lockManager
) => {
  if (!storage || !key) {
    return {workspace: null, discarded: false, storageError: true};
  }
  const result = await runWithStorageLock(lockManager, key, () => {
    let serialized;
    try {
      serialized = storage.getItem(key);
    } catch {
      return {workspace: null, discarded: false, storageError: true};
    }
    if (!serialized) {
      return {workspace: null, discarded: false, storageError: false};
    }
    if (utf8ByteLength(serialized) > UI_TRANSLATION_WORKSPACE_MAX_BYTES) {
      safelyRemoveWorkspace(storage, key);
      return {workspace: null, discarded: true, storageError: false};
    }

    try {
      const workspace = normalizeUiTranslationWorkspace(JSON.parse(serialized));
      if (!workspace) throw new Error('Invalid workspace');
      return {workspace, discarded: false, storageError: false};
    } catch {
      safelyRemoveWorkspace(storage, key);
      return {workspace: null, discarded: true, storageError: false};
    }
  });
  return result.lockUnavailable
    ? {
        workspace: null,
        discarded: false,
        storageError: false,
        lockUnavailable: true,
      }
    : result;
};

export const saveUiTranslationWorkspace = async (
  storage,
  key,
  workspace,
  lockManager
) => {
  const normalized = normalizeUiTranslationWorkspace({
    ...workspace,
    savedAt: new Date().toISOString(),
  });
  if (!storage || !key || !normalized) {
    return {
      saved: false,
      workspace: normalized,
      conflict: false,
      storageError: true,
    };
  }

  const result = await runWithStorageLock(lockManager, key, () => {
    try {
    const current = readStoredWorkspaceForWrite(storage, key);
    const expectedCurrent = {
      storageRevision: normalized.storageRevision,
      writeToken: normalized.writeToken,
    };
    const currentIdentity = current.workspace
      ? {
          storageRevision: current.workspace.storageRevision,
          writeToken: current.workspace.writeToken,
        }
      : {storageRevision: 0, writeToken: ''};
    if (
      current.invalid ||
      currentIdentity.storageRevision !== expectedCurrent.storageRevision ||
      currentIdentity.writeToken !== expectedCurrent.writeToken
    ) {
      return {
        saved: false,
        workspace: normalized,
        currentWorkspace: current.workspace,
        conflict: true,
        storageError: false,
      };
    }

    if (!normalized.changes.length) {
      storage.removeItem(key);
      const clearedWorkspace = normalizeUiTranslationWorkspace({
        ...normalized,
        storageRevision: 0,
        writeToken: '',
      });
      return {
        saved: true,
        workspace: clearedWorkspace,
        conflict: false,
        storageError: false,
        deleted: true,
      };
    } else {
      const nextWorkspace = normalizeUiTranslationWorkspace({
        ...normalized,
        storageRevision: currentIdentity.storageRevision + 1,
        writeToken: createStorageWriteToken(),
      });
      storage.setItem(key, JSON.stringify(nextWorkspace));
      return {
        saved: true,
        workspace: nextWorkspace,
        conflict: false,
        storageError: false,
        deleted: false,
      };
    }
    } catch {
      return {
        saved: false,
        workspace: normalized,
        conflict: false,
        storageError: true,
      };
    }
  });
  return result.lockUnavailable
    ? {
        saved: false,
        workspace: normalized,
        conflict: false,
        storageError: false,
        lockUnavailable: true,
      }
    : result;
};

export const upsertUiTranslationChange = (workspace, change) => {
  const normalizedChange = normalizeChange(change);
  if (!workspace || !normalizedChange) return workspace;
  const changes = workspace.changes.filter(
    (candidate) => candidate.key !== normalizedChange.key
  );
  changes.push(normalizedChange);
  return normalizeUiTranslationWorkspace({
    ...workspace,
    changes,
    pendingPublication: null,
  });
};

export const removeUiTranslationChanges = (workspace, keys) => {
  if (!workspace) return workspace;
  const removed = new Set(keys || []);
  return normalizeUiTranslationWorkspace({
    ...workspace,
    changes: workspace.changes.filter((change) => !removed.has(change.key)),
    pendingPublication: null,
  });
};

export const clearSubmittedUiTranslationChanges = (
  workspace,
  submittedKeys,
  {catalogVersion, baseRevision}
) => {
  const remaining = removeUiTranslationChanges(workspace, submittedKeys);
  if (!remaining) return remaining;
  return normalizeUiTranslationWorkspace({
    ...remaining,
    catalogVersion: catalogVersion || remaining.catalogVersion,
    baseRevision:
      Number.isSafeInteger(baseRevision) && baseRevision >= 0
        ? baseRevision
        : remaining.baseRevision,
    pendingPublication: null,
  });
};

export const rebaseUiTranslationWorkspace = (
  workspace,
  {catalogVersion, baseRevision, entries}
) => {
  const entryByKey = new Map(
    (Array.isArray(entries) ? entries : []).map((entry) => [entry.key, entry])
  );
  const conflicts = [];

  workspace.changes.forEach((change) => {
    const entry = entryByKey.get(change.key);
    const currentHash =
      typeof entry?.currentValueHash === 'string'
        ? entry.currentValueHash
        : typeof entry?.currentHash === 'string'
          ? entry.currentHash
          : '';
    const currentOwnership = normalizeOwnership(entry?.ownership);
    const sourceChanged = Boolean(
      change.sourceHash && entry?.sourceHash && change.sourceHash !== entry.sourceHash
    );
    const ownershipChanged = change.baseOwnership !== currentOwnership;
    const liveChanged = !entry || change.baseHash !== currentHash || ownershipChanged;

    if (sourceChanged || liveChanged) {
      conflicts.push({
        key: change.key,
        source: entry?.source || '',
        localValue:
          change.action === 'RESET_TO_GENERATED'
            ? entry?.currentValue ?? entry?.value ?? ''
            : change.value,
        action: change.action,
        currentValue: entry?.currentValue ?? entry?.value ?? '',
        currentValueHash: currentHash,
        currentOwnership,
        sourceHash: entry?.sourceHash || '',
        removedFromCatalog: !entry,
      });
    }
  });

  return {
    workspace: normalizeUiTranslationWorkspace({
      ...workspace,
      catalogVersion,
      baseRevision,
      pendingPublication: null,
    }),
    conflicts,
    rebased:
      workspace.catalogVersion !== catalogVersion ||
      workspace.baseRevision !== baseRevision,
  };
};

export const resolveUiTranslationConflicts = (
  workspace,
  conflicts,
  choices,
  {catalogVersion, baseRevision}
) => {
  const conflictByKey = new Map(
    (Array.isArray(conflicts) ? conflicts : []).map((conflict) => [
      conflict.key,
      conflict,
    ])
  );
  const unresolvedKeys = [];
  const changes = [];

  workspace.changes.forEach((change) => {
    const conflict = conflictByKey.get(change.key);
    if (!conflict) {
      changes.push(change);
      return;
    }
    const choice = choices?.[change.key];
    if (choice === 'production') return;
    if (choice !== 'mine' || conflict.removedFromCatalog) {
      unresolvedKeys.push(change.key);
      changes.push(change);
      return;
    }
    changes.push({
      ...change,
      baseHash: conflict.currentValueHash || '',
      baseOwnership: normalizeOwnership(conflict.currentOwnership),
      sourceHash: conflict.sourceHash || change.sourceHash,
    });
  });

  return {
    workspace: normalizeUiTranslationWorkspace({
      ...workspace,
      catalogVersion: catalogVersion || workspace.catalogVersion,
      baseRevision,
      changes,
      pendingPublication: null,
    }),
    unresolvedKeys,
  };
};

const hashRequestPart = (value, seed) => {
  const hash = value.split('').reduce((current, character) => {
    // FNV-1a is intentionally a 32-bit hash for a local request fingerprint.
    // eslint-disable-next-line no-bitwise
    const multiplied = Math.imul(current ^ character.charCodeAt(0), 16777619);
    return new Uint32Array([multiplied])[0];
  }, new Uint32Array([seed])[0]);
  return hash.toString(16).padStart(8, '0');
};

export const createUiTranslationRollbackFingerprint = (body) => {
  const canonical = JSON.stringify({
    locale: body?.locale || '',
    targetRevision: body?.targetRevision ?? null,
    catalogVersion: body?.catalogVersion || '',
  });
  return `${canonical.length.toString(16)}:${hashRequestPart(
    canonical,
    2166136261
  )}:${hashRequestPart(canonical, 3339675911)}`;
};

const normalizeRollbackAttempt = (candidate) => {
  if (!isObject(candidate)) return null;
  const savedAtTime = Date.parse(candidate.savedAt);
  if (
    typeof candidate.fingerprint !== 'string' ||
    !/^[a-f0-9:-]{1,128}$/i.test(candidate.fingerprint) ||
    typeof candidate.idempotencyKey !== 'string' ||
    candidate.idempotencyKey.length < 8 ||
    candidate.idempotencyKey.length > 200 ||
    typeof candidate.savedAt !== 'string' ||
    !Number.isFinite(savedAtTime) ||
    savedAtTime > Date.now() + 24 * 60 * 60 * 1000 ||
    Date.now() - savedAtTime > UI_TRANSLATION_WORKSPACE_MAX_AGE_MS
  ) {
    return null;
  }
  const storageIdentity = normalizeStorageIdentity(candidate);
  if (!storageIdentity) return null;
  return {
    fingerprint: candidate.fingerprint,
    idempotencyKey: candidate.idempotencyKey,
    savedAt: candidate.savedAt,
    ...storageIdentity,
  };
};

export const loadUiTranslationRollbackAttempt = async (
  storage,
  key,
  lockManager
) => {
  if (!storage || !key) return {attempt: null, storageError: true};
  const result = await runWithStorageLock(lockManager, key, () => {
    try {
      const serialized = storage.getItem(key);
      if (!serialized) return {attempt: null, storageError: false};
      if (utf8ByteLength(serialized) > 2048) throw new Error('Oversized attempt');
      const attempt = normalizeRollbackAttempt(JSON.parse(serialized));
      if (!attempt) throw new Error('Invalid attempt');
      return {attempt, storageError: false};
    } catch {
      safelyRemoveWorkspace(storage, key);
      return {attempt: null, storageError: true};
    }
  });
  return result.lockUnavailable
    ? {attempt: null, storageError: false, lockUnavailable: true}
    : result;
};

export const prepareUiTranslationRollbackAttempt = async (
  storage,
  key,
  fingerprint,
  createIdempotencyKey,
  lockManager
) => {
  if (!storage || !key) {
    return {
      attempt: null,
      storageError: true,
      saved: false,
      reused: false,
      conflict: false,
    };
  }
  const result = await runWithStorageLock(lockManager, key, () => {
  let loaded;
  try {
    const serialized = storage.getItem(key);
    const attempt = serialized
      ? normalizeRollbackAttempt(JSON.parse(serialized))
      : null;
    if (serialized && !attempt) throw new Error('Invalid attempt');
    loaded = {attempt, storageError: false};
  } catch {
    return {
      attempt: null,
      storageError: true,
      saved: false,
      reused: false,
      conflict: false,
    };
  }
  if (loaded.attempt?.fingerprint === fingerprint) {
    return {...loaded, saved: true, reused: true, conflict: false};
  }
  if (loaded.attempt) {
    return {
      ...loaded,
      saved: false,
      reused: false,
      conflict: true,
    };
  }
  const attempt = {
    fingerprint,
    idempotencyKey: createIdempotencyKey(),
    savedAt: new Date().toISOString(),
    storageRevision: 1,
    writeToken: createStorageWriteToken(),
  };
  try {
    storage.setItem(key, JSON.stringify(attempt));
    return {
      attempt,
      storageError: false,
      saved: true,
      reused: false,
      conflict: false,
    };
  } catch {
    return {
      attempt: null,
      storageError: true,
      saved: false,
      reused: false,
      conflict: false,
    };
  }
  });
  return result.lockUnavailable
    ? {
        attempt: null,
        storageError: false,
        saved: false,
        reused: false,
        conflict: false,
        lockUnavailable: true,
      }
    : result;
};

export const clearUiTranslationRollbackAttempt = async (
  storage,
  key,
  expectedAttempt,
  lockManager
) => {
  if (!storage || !key || !expectedAttempt) {
    return {cleared: false, conflict: false, storageError: true};
  }
  const result = await runWithStorageLock(lockManager, key, () => {
    try {
      const serialized = storage.getItem(key);
      if (!serialized) {
        return {cleared: true, conflict: false, storageError: false};
      }
      const attempt = normalizeRollbackAttempt(JSON.parse(serialized));
      if (!attempt) {
        return {cleared: false, conflict: false, storageError: true};
      }
    if (
        attempt.fingerprint !== expectedAttempt.fingerprint ||
        attempt.storageRevision !== expectedAttempt.storageRevision ||
        attempt.writeToken !== expectedAttempt.writeToken
    ) {
      return {cleared: false, conflict: true, storageError: false};
    }
    storage.removeItem(key);
    return {cleared: true, conflict: false, storageError: false};
    } catch {
      return {cleared: false, conflict: false, storageError: true};
    }
  });
  return result.lockUnavailable
    ? {cleared: false, conflict: false, storageError: false, lockUnavailable: true}
    : result;
};

export const createUiTranslationPublicationFingerprint = (body) => {
  const changes = Array.isArray(body?.changes)
    ? [...body.changes].sort((left, right) =>
        String(left.key).localeCompare(String(right.key))
      )
    : [];
  const canonical = JSON.stringify({
    locale: body?.locale || '',
    namespace: body?.namespace || '',
    catalogVersion: body?.catalogVersion || '',
    baseRevision: body?.baseRevision ?? null,
    changes,
  });
  return `${canonical.length.toString(16)}:${hashRequestPart(
    canonical,
    2166136261
  )}:${hashRequestPart(canonical, 3339675911)}`;
};

export const prepareUiTranslationPublication = (
  workspace,
  fingerprint,
  createIdempotencyKey
) => {
  if (
    workspace.pendingPublication?.fingerprint === fingerprint &&
    workspace.pendingPublication.idempotencyKey
  ) {
    return {
      workspace,
      idempotencyKey: workspace.pendingPublication.idempotencyKey,
      reused: true,
    };
  }
  const idempotencyKey = createIdempotencyKey();
  const nextWorkspace = normalizeUiTranslationWorkspace({
    ...workspace,
    pendingPublication: {fingerprint, idempotencyKey},
  });
  return {workspace: nextWorkspace, idempotencyKey, reused: false};
};

const normalizeVariable = (variable) =>
  String(variable || '')
    .replace(/^{{\s*/, '')
    .replace(/\s*}}$/, '')
    .trim();

export const extractUiTranslationVariables = (value) => {
  const variables = [];
  const expression = /{{\s*([^{}]+?)\s*}}/g;
  let match = expression.exec(value);
  while (match) {
    variables.push(normalizeVariable(match[1]));
    match = expression.exec(value);
  }
  return variables;
};

export const validateUiTranslationValue = (entry, value) => {
  if (typeof value !== 'string') return 'Enter a text value.';
  const normalized = value.replace(/\r\n?/g, '\n').normalize('NFC');
  const hasUnsupportedControl = Array.from(normalized).some((character) => {
    const code = character.charCodeAt(0);
    return (
      code <= 0x08 ||
      code === 0x0b ||
      code === 0x0c ||
      (code >= 0x0e && code <= 0x1f) ||
      code === 0x7f
    );
  });
  if (utf8ByteLength(normalized) > UI_TRANSLATION_MAX_VALUE_BYTES) {
    return `Use no more than ${UI_TRANSLATION_MAX_VALUE_BYTES} UTF-8 bytes.`;
  }
  if (entry?.required && !normalized.trim()) return 'A translation is required.';
  if (
    /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/.test(
      normalized
    ) ||
    hasUnsupportedControl
  ) {
    return 'Remove unsupported control characters.';
  }
  if (/[<>]/.test(normalized)) {
    return 'Angle brackets and HTML are not supported in UI translations.';
  }

  const expected = (Array.isArray(entry?.variables) ? entry.variables : [])
    .map(normalizeVariable)
    .filter(Boolean)
    .sort();
  const actual = extractUiTranslationVariables(normalized).filter(Boolean).sort();
  const malformedTokens = normalized
    .replace(/{{\s*([^{}]+?)\s*}}/g, '')
    .match(/{{|}}/);
  if (
    malformedTokens ||
    expected.length !== actual.length ||
    expected.some((variable, index) => variable !== actual[index])
  ) {
    const tokens = expected.map((variable) => `{{${variable}}}`).join(', ');
    return tokens
      ? `Keep these variables unchanged: ${tokens}.`
      : 'Remove unsupported interpolation variables.';
  }
  return '';
};
